import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import config from '../config/config';
import cacheService from '../services/cacheService';

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  position: relative;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const FilterList = styled.div`
  display: flex;
  gap: 10px;
  padding: 5px;
  flex-wrap: nowrap;
  min-width: min-content;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const FilterItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 25px;
  background-color: ${props => props.selected ? '#F38530' : 'rgba(255, 255, 255, 0.95)'};
  color: ${props => props.selected ? 'white' : '#14464F'};
  cursor: pointer;
  white-space: nowrap;
  font-size: 15px;
  font-weight: ${props => props.selected ? '700' : '500'};
  letter-spacing: 0.3px;
  text-shadow: ${props => props.selected ? '0px 1px 2px rgba(0, 0, 0, 0.2)' : 'none'};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: scale(1);
  border: ${props => props.selected ? '0px' : '1px solid rgba(20, 70, 79, 0.1)'};
  
  &:hover {
    transform: scale(1.02);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    background-color: ${props => props.selected ? '#e07428' : 'rgba(255, 255, 255, 1)'};
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  @media (max-width: 768px) {
    padding: 8px 14px;
    font-size: 13px;
    gap: 6px;
  }
`;

const IconImage = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
  transition: all 0.3s ease;
  filter: ${props => props.selected 
    ? 'brightness(0) invert(1)' 
    : 'brightness(0) saturate(100%) invert(56%) sepia(93%) saturate(385%) hue-rotate(334deg) brightness(97%) contrast(94%)'
  };
  
  @media (max-width: 768px) {
    width: 16px;
    height: 16px;
  }
`;

const FilterComponent = ({ onFilterChange, selectedFilters }) => {
  const { language } = useLanguage();
  const [amenities, setAmenities] = useState([]);

  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        // Use cache service for amenities
        const amenitiesData = await cacheService.getAmenities();
        setAmenities(amenitiesData);
        
        // Select the first filter by default if there are amenities
        if (amenitiesData.length > 0 && selectedFilters.length === 0) {
          onFilterChange([amenitiesData[0].id]);
        }
      } catch (error) {
        console.error('Error fetching amenities:', error);
      }
    };

    fetchAmenities();
  }, []);

  const handleFilterClick = (filterId) => {
    const newFilters = selectedFilters.includes(filterId)
      ? selectedFilters.filter(f => f !== filterId)
      : [...selectedFilters, filterId];
    
    onFilterChange(newFilters);
  };

  const getAmenityName = (amenity) => {
    if (language === 'es' && amenity.name_es) {
      return amenity.name_es;
    }
    return amenity.name;
  };

  return (
    <FilterContainer>
      <FilterList>
        {amenities.map(amenity => (
          <FilterItem
            key={amenity.id}
            selected={selectedFilters.includes(amenity.id)}
            onClick={() => handleFilterClick(amenity.id)}
          >
            <IconImage 
              src={amenity.icon} 
              alt={getAmenityName(amenity)}
              selected={selectedFilters.includes(amenity.id)}
            />
            {getAmenityName(amenity)}
          </FilterItem>
        ))}
      </FilterList>
    </FilterContainer>
  );
};

export default FilterComponent;
